// =====================================================
// components/MediaViewers.tsx
// SafeImage, MobileVideoPlayer, VideoThumbPreview
// MediaViewer, ZoomImageViewer
// =====================================================

import React, { useState, useEffect, useRef } from 'react';
import {
  StyleSheet, View, Image, Modal, TouchableOpacity,
  Platform, Animated, Dimensions, Text,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { VideoView, useVideoPlayer } from 'expo-video';
import * as Haptics from 'expo-haptics';

const { width, height } = Dimensions.get('window');
const NATIVE = Platform.OS !== 'web';

// ─── SafeImage ────────────────────────────────────────────────────────────────
export function SafeImage({ uri, style, resizeMode = 'cover', fallback = null }: {
  uri: string | null | undefined;
  style: any;
  resizeMode?: 'cover' | 'contain' | 'stretch' | 'center';
  fallback?: React.ReactNode;
}) {
  const [error, setError] = useState(false);
  useEffect(() => { setError(false); }, [uri]);
  if (!uri || error) return fallback ? <>{fallback}</> : null;
  return (
    <Image
      source={{ uri }}
      style={style}
      resizeMode={resizeMode}
      onError={() => setError(true)}
    />
  );
}

// ─── MobileVideoPlayer ───────────────────────────────────────────────────────
export function MobileVideoPlayer({ url }: { url: string }) {
  const player = useVideoPlayer({ uri: url }, p => { p.play(); });
  return (
    <VideoView
      player={player}
      style={styles.nativeVideo}
      contentFit="contain"
      nativeControls
    />
  );
}

// ─── VideoThumbPreview ───────────────────────────────────────────────────────
export function VideoThumbPreview({ uri, isActive, style }: {
  uri: string; isActive: boolean; style: any;
}) {
  const [hasError, setHasError] = useState(false);
  const player = useVideoPlayer({ uri }, p => {
    p.loop  = true;
    p.muted = true;
    if (isActive) p.play();
  });
  useEffect(() => {
    if (hasError) return;
    try {
      if (isActive) player.play();
      else          player.pause();
    } catch {}
  }, [isActive, hasError]);
  if (hasError) return <View style={[style, { backgroundColor: '#1A1A2E' }]} />;
  return (
    <VideoView
      player={player}
      style={style}
      contentFit="cover"
      nativeControls={false}
      onError={() => setHasError(true)}
    />
  );
}

// ─── MediaViewer ─────────────────────────────────────────────────────────────
export function MediaViewer({ visible, url, type, onClose }: {
  visible: boolean; url: string | null; type: 'image' | 'video'; onClose: () => void;
}) {
  if (!url) return null;
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose} statusBarTranslucent>
      <View style={styles.overlay}>
        <TouchableOpacity style={styles.closeBtn} onPress={onClose}>
          <Ionicons name="close" size={30} color="#fff" />
        </TouchableOpacity>
        {type === 'image' ? (
          <TouchableOpacity activeOpacity={1} onPress={onClose} style={styles.imageWrapper}>
            <SafeImage
              uri={url}
              style={styles.image}
              resizeMode="contain"
              fallback={
                <View style={[styles.image, { justifyContent: 'center', alignItems: 'center' }]}>
                  <Ionicons name="image-outline" size={48} color="rgba(255,255,255,0.3)" />
                </View>
              }
            />
          </TouchableOpacity>
        ) : Platform.OS === 'web' ? (
          // @ts-ignore
          <video src={url} controls autoPlay style={{ width: '100%', maxHeight: '90%', objectFit: 'contain', backgroundColor: '#000' }} />
        ) : (
          <MobileVideoPlayer url={url} />
        )}
      </View>
    </Modal>
  );
}

// ─── ZoomImageViewer ──────────────────────────────────────────────────────────
export function ZoomImageViewer({ visible, url, onClose }: {
  visible: boolean; url: string | null; onClose: () => void;
}) {
  const scaleAnim   = useRef(new Animated.Value(0.1)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;
  const isClosing   = useRef(false);

  useEffect(() => {
    if (visible) {
      isClosing.current = false;
      scaleAnim.setValue(0.1);
      opacityAnim.setValue(0);
      Animated.parallel([
        Animated.spring(scaleAnim,  { toValue: 1, useNativeDriver: true, tension: 80, friction: 8 }),
        Animated.timing(opacityAnim,{ toValue: 1, duration: 180, useNativeDriver: true }),
      ]).start();
    }
  }, [visible]);

  const handleClose = () => {
    if (isClosing.current) return;
    isClosing.current = true;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    Animated.parallel([
      Animated.spring(scaleAnim,  { toValue: 0.1, useNativeDriver: true, tension: 100, friction: 8 }),
      Animated.timing(opacityAnim,{ toValue: 0, duration: 160, useNativeDriver: true }),
    ]).start(() => onClose());
  };

  if (!url) return null;

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={handleClose} statusBarTranslucent>
      <Animated.View style={[styles.zoomOverlay, { opacity: opacityAnim }]}>
        <TouchableOpacity style={styles.closeBtn} onPress={handleClose}>
          <Ionicons name="close" size={30} color="#fff" />
        </TouchableOpacity>
        <TouchableOpacity activeOpacity={1} onPress={handleClose} style={styles.imageWrapper}>
          <Animated.View style={[styles.imageWrapper, { transform: [{ scale: scaleAnim }] }]}>
            <SafeImage uri={url} style={styles.image} resizeMode="contain"
              fallback={
                <View style={[styles.image, { justifyContent: 'center', alignItems: 'center' }]}>
                  <Ionicons name="image-outline" size={48} color="rgba(255,255,255,0.3)" />
                </View>
              }
            />
          </Animated.View>
        </TouchableOpacity>
      </Animated.View>
    </Modal>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  overlay:      { flex: 1, backgroundColor: 'rgba(0,0,0,0.97)', justifyContent: 'center', alignItems: 'center' },
  zoomOverlay:  { flex: 1, backgroundColor: 'rgba(0,0,0,0.97)', justifyContent: 'center', alignItems: 'center' },
  closeBtn:     { position: 'absolute', top: Platform.OS === 'ios' ? 60 : 40, right: 20, zIndex: 10, padding: 8, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20 },
  imageWrapper: { width, height: height * 0.85, justifyContent: 'center', alignItems: 'center' },
  image:        { width, height: height * 0.85 },
  nativeVideo:  { width, height: height * 0.75 },
});

